import InputDate from "@/Components/Base/Input/InputDate";
import InputPreco from "@/Components/Base/Input/InputPreco";
import ModalBase, { IModalBaseProps, ModalBaseRefType } from "@/Components/Base/ModalBase";
import { usePagamentoListarPorFatura } from "@/Services/Financeiro/Titulo/Pagamento/PagamentoServices";
import { LancamentoReembolsoType, useLancamentoAReceberReembolsar } from "@/Services/Financeiro/Titulo/ParcelaService";
import { Col, Collapse, Descriptions, Form, FormInstance, FormProps, Input, InputNumber, List, notification, Row, Switch } from "antd";
import { forwardRef, useEffect, useRef } from "react";
import PagamentoListItem from "./Pagamento/PagamentoListItem";
import PagamentoTipo from "@/Models/Financeiro/Titulo/Pagamento/PagamentoTipo";
import { PropriedadesDeNotificacao } from "@/Utils/Notificacoes";
import Titulo from "@/Models/Financeiro/Titulo/Titulo";

interface ITituloReembolsoFormModalProps extends Omit<IModalBaseProps, "children"> {
    codFatura:number;
    ref?:React.RefObject<ModalBaseRefType>;
    formProps?:FormProps<LancamentoReembolsoType>;
    onSuccess?:(variables: Titulo) => void;
}

const TituloReembolsoFormModal = forwardRef<ModalBaseRefType, ITituloReembolsoFormModalProps>(function TituloFormDrawer(
    {
        width, 
        destroyOnClose,
        codFatura,
        formProps,
        onSuccess : onSuccessParam,
        ...rest
    }, 
    refBase
) { 
    width = width ?? "100%";
    destroyOnClose = destroyOnClose ?? true;

    const refNova = useRef<ModalBaseRefType>(null);
    const ref = refBase ?? refNova;

    const [newForm] = Form.useForm<LancamentoReembolsoType>();
    const form = formProps?.form ?? newForm;

    const tituloPagamentosFormValue = Form.useWatch(["pagamentos"], form);
    const gerarParcelasComBaseNaReceitaFormValue = Form.useWatch(["gerarParcelasComBaseNaReceita"], form);

    const {
        mutate,
        isPending
    } = useLancamentoAReceberReembolsar({
        onSuccess(data, variables, context) {
            notification.success({
                ...PropriedadesDeNotificacao["Sucesso"],
                message : "Reembolso gerado com sucesso."
            });

            refNova.current?.fechar();

            onSuccessParam?.(data.data);
        },
    });

    const {
        data : pagamentos,
        isLoading : isLoadingPagamentos
    } = usePagamentoListarPorFatura({
        codFatura : codFatura
    });
    
    useEffect(() => {
        pagamentos?.forEach(f => {
            f.titulo = null;
            const tipoNome = f.tipo?.nome ?? '';
            f.tipo = { } as PagamentoTipo;
            f.tipo.nome = tipoNome;
        });
        form.setFieldValue("pagamentos", pagamentos);
    }, [pagamentos]);

    return <ModalBase
        {...rest}
        ref={ref}
        width={width}
        destroyOnClose={destroyOnClose}
        confirmLoading={isLoadingPagamentos || isPending}
        onCancel={() => {
            form.resetFields();
        }}
        onOk={() => {
            form.submit();
        }}
    >
        <Form
            {...formProps}
            form={form}
            disabled={formProps?.disabled}
            layout={formProps?.layout ?? "vertical"}
            size={"small"}
            onFinish={(lancamento) => {
                lancamento.codFatura = codFatura;
                mutate(lancamento);
            }}
            labelCol={{
                style : {
                    fontWeight : "bold"
                }
            }}
            initialValues={{
                ...formProps?.initialValues,
                gerarParcelasComBaseNaReceita : formProps?.initialValues?.gerarParcelasComBaseNaReceita ?? true,
                codFatura : codFatura,
                dataPrimeiroReembolso : formProps?.initialValues?.dataPrimeiroReembolso === true ? true : false
            }}
        >
            <Row gutter={8}>
                <Form.Item label="Código Título" hidden name={["codFatura"]}>
                    <Input disabled hidden />
                </Form.Item>
                <Col xs={24}>
                    <Form.Item name={["gerarParcelasComBaseNaReceita"]} rules={[{ required : true }]} label="Gerar parcelas com base na receita?">
                        <Switch />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={8} lg={4}>
                    <Form.Item name={["dataPrimeiroReembolso"]} rules={[{ required : true }]} label="Dt. Primeiro Reembolso">
                        <InputDate />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={8} lg={4}>
                    <Form.Item name={["valorTotal"]} rules={[{ required : true }]} label="Valor Reembolsado (R$)">
                        <InputPreco />
                    </Form.Item>
                </Col>
                {!gerarParcelasComBaseNaReceitaFormValue && <Col xs={24} sm={8} md={8} lg={4}>
                    <Form.Item name={["qtdParcelas"]} rules={[{ required : true }]} label="Qtd. Parcelas">
                        <InputNumber />
                    </Form.Item>
                </Col>}
                <Col xs={24}>
                    <Collapse
                        size="small"
                        destroyInactivePanel={false}
                        items={[
                            {
                                key : "pagamentos",
                                label : "Pagamentos",
                                children : <Form.List 
                                    name={"pagamentos"}
                                >
                                    {(fields, { add, remove }, { errors, warnings }) => (
                                        fields.map((field, index) => 
                                            <PagamentoListItem
                                                key={index}
                                                data={(pagamentos ?? [])[index]}
                                                avatar={
                                                    <Col xs={4} key={field.key}>
                                                        <Form.Item label="Código" hidden name={[field.name, "codigo"]}>
                                                            <Input disabled hidden />
                                                        </Form.Item>
                                                        <Form.Item label="Parcela" hidden name={[field.name, "codTitulo"]}>
                                                            <Input disabled hidden />
                                                        </Form.Item>
                                                        <Form.Item label="Tipo" hidden name={[field.name, "codTipo"]}>
                                                            <Input disabled hidden />
                                                        </Form.Item>
                                                        <Form.Item label="Conta" hidden name={[field.name, "codConta"]}>
                                                            <Input disabled hidden />
                                                        </Form.Item>
                                                        <Form.Item 
                                                            name={[field.name, "identificadorProvedorCancelamento"]} 
                                                            rules={[{ required : false }]} 
                                                            label="Ident. Banco (Cancelamento)"
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                }
                                            />
                                        )
                                    )}
                                </Form.List>
                            }
                        ]}
                    />
                </Col>

                {process.env.NODE_ENV === "development" && <Col xs={24}>
                    <Collapse
                        size="small"
                        destroyInactivePanel={false}
                        items={[
                            {
                                key : "dados-json",
                                label : "Dados",
                                children : <Form.Item shouldUpdate>
                                    {() => {
                                        return <>
                                            <code>
                                                <pre>{JSON.stringify(form.getFieldsValue(), null, 4)}</pre>
                                            </code>
                                        </> 
                                    }}
                                </Form.Item>
                            }
                        ]}
                    />
                </Col>}
            </Row>
        </Form>
    </ModalBase>;
});

export default TituloReembolsoFormModal